import React, { useState, useEffect, useRef } from 'react';

const ScrollableImageGallery = ({
  images = [],
  spacing = 12
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const scrollContainerRef = useRef(null);
  const modalScrollRef = useRef(null);
  const imageRef = useRef(null);

  const displayImages = images;

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.offsetWidth;
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      // Calculate the index based on the full container width for each "page"
      const newIndex = Math.round(scrollLeft / containerWidth);
      setCurrentIndex(newIndex);
    }
  };

  const scrollToIndex = (index) => {
    if (scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.offsetWidth;
      const scrollLeft = index * containerWidth;
      scrollContainerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const openModal = (index) => {
    setModalIndex(index);
    setModalOpen(true);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const closeModal = () => {
    setModalOpen(false);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
    document.body.style.overflow = 'unset';
  };

  const handleModalScroll = () => {
    if (modalScrollRef.current) {
      const containerWidth = modalScrollRef.current.offsetWidth;
      const scrollLeft = modalScrollRef.current.scrollLeft;
      const newIndex = Math.round(scrollLeft / containerWidth);
      setModalIndex(newIndex);
    }
  };

  const scrollToModalIndex = (index) => {
    if (modalScrollRef.current) {
      const containerWidth = modalScrollRef.current.offsetWidth;
      const scrollLeft = index * containerWidth;
      modalScrollRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Zoom functionality
  const handleZoom = (delta, clientX, clientY) => {
    if (!imageRef.current) return;
    
    const rect = imageRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    const newZoom = Math.min(Math.max(zoomLevel + delta, 1), 4);
    
    if (newZoom === 1) {
      setPanOffset({ x: 0, y: 0 });
    } else {
      // Adjust pan offset to zoom towards the cursor
      const zoomRatio = newZoom / zoomLevel;
      setPanOffset(prev => ({
        x: (prev.x - x) * zoomRatio + x,
        y: (prev.y - y) * zoomRatio + y
      }));
    }
    
    setZoomLevel(newZoom);
  };

  // Touch/Mouse event handlers for zooming and panning
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Handle pinch zoom start
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) + 
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      setDragStart({ distance, zoom: zoomLevel });
    } else if (e.touches.length === 1 && zoomLevel > 1) {
      // Handle pan start
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - panOffset.x,
        y: e.touches[0].clientY - panOffset.y
      });
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    
    if (e.touches.length === 2) {
      // Handle pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) + 
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      
      const scale = distance / dragStart.distance;
      const newZoom = Math.min(Math.max(dragStart.zoom * scale, 1), 4);
      setZoomLevel(newZoom);
      
      if (newZoom === 1) {
        setPanOffset({ x: 0, y: 0 });
      }
    } else if (isDragging && e.touches.length === 1 && zoomLevel > 1) {
      // Handle pan
      setPanOffset({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Mouse event handlers
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - panOffset.x,
        y: e.clientY - panOffset.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setPanOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    handleZoom(delta, e.clientX, e.clientY);
  };

  // Double tap to zoom
  const handleDoubleClick = (e) => {
    if (zoomLevel === 1) {
      handleZoom(1, e.clientX, e.clientY);
    } else {
      setZoomLevel(1);
      setPanOffset({ x: 0, y: 0 });
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!modalOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'ArrowLeft' && modalIndex > 0) {
        scrollToModalIndex(modalIndex - 1);
      } else if (e.key === 'ArrowRight' && modalIndex < displayImages.length - 1) {
        scrollToModalIndex(modalIndex + 1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, modalIndex, displayImages.length]);

  return (
    <>
      <div className="krunk-mt-4 krunk-relative krunk-w-full">
        <div
          ref={scrollContainerRef}
          className="krunk-overflow-x-scroll krunk-no-scrollbar krunk-flex krunk-h-48"
          onScroll={handleScroll}
          style={{
            scrollSnapType: 'x mandatory',
            gap: '0px',
            flexWrap: 'nowrap'
          }}
        >
          {displayImages.map((image, index) => {
            const imageSrc = typeof image === 'string' ? image : image.src || image.url;
            const imageAlt = typeof image === 'object' ? image.alt || `Gallery image ${index + 1}` : `Gallery image ${index + 1}`;

            return (
              <div
                key={index}
                className="krunk-flex-shrink-0 krunk-w-full krunk-h-full krunk-flex krunk-items-center krunk-justify-center krunk-cursor-pointer"
                style={{
                  margin: '0',
                  padding: '0',
                  scrollSnapAlign: 'start'
                }}
                onClick={() => openModal(index)}
              >
                <img
                  src={imageSrc}
                  alt={imageAlt}
                  className="krunk-w-full krunk-h-full krunk-object-cover"
                />
              </div>
            );
          })}
        </div>

        <div className="krunk-flex krunk-justify-center krunk-mt-4 krunk-gap-2">
          {displayImages.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              className={`krunk-w-2 krunk-h-2 krunk-rounded-full krunk-transition-colors ${
                index === currentIndex ? 'krunk-bg-blue-500' : 'krunk-bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="krunk-fixed krunk-inset-0 krunk-bg-black krunk-bg-opacity-95 krunk-z-50 krunk-flex krunk-flex-col">
          {/* Header with close button */}
          <div className="krunk-absolute krunk-top-0 krunk-left-0 krunk-right-0 krunk-z-10 krunk-flex krunk-justify-end krunk-items-center krunk-p-4 krunk-bg-gradient-to-b krunk-from-black krunk-from-50% krunk-to-transparent">
            <button
              onClick={closeModal}
              className="krunk-text-white krunk-text-2xl krunk-w-8 krunk-h-8 krunk-flex krunk-items-center krunk-justify-center krunk-rounded-full krunk-bg-black krunk-bg-opacity-50 hover:krunk-bg-opacity-70 krunk-transition-all"
            >
              ×
            </button>
          </div>

          {/* Image container */}
          <div
            ref={modalScrollRef}
            className="krunk-flex-1 krunk-overflow-x-scroll krunk-no-scrollbar krunk-flex"
            onScroll={handleModalScroll}
            style={{
              scrollSnapType: 'x mandatory',
              gap: '0px',
              flexWrap: 'nowrap'
            }}
          >
            {displayImages.map((image, index) => {
              const imageSrc = typeof image === 'string' ? image : image.src || image.url;
              const imageAlt = typeof image === 'object' ? image.alt || `Gallery image ${index + 1}` : `Gallery image ${index + 1}`;

              return (
                <div
                  key={index}
                  className="krunk-flex-shrink-0 krunk-w-full krunk-h-full krunk-flex krunk-items-center krunk-justify-center krunk-overflow-hidden"
                  style={{
                    scrollSnapAlign: 'start'
                  }}
                >
                  <img
                    ref={index === modalIndex ? imageRef : null}
                    src={imageSrc}
                    alt={imageAlt}
                    className="krunk-max-w-full krunk-max-h-full krunk-object-contain krunk-select-none"
                    style={{
                      transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
                      cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
                      transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onWheel={handleWheel}
                    onDoubleClick={handleDoubleClick}
                    draggable={false}
                  />
                </div>
              );
            })}
          </div>

          {/* Navigation arrows */}
          {displayImages.length > 1 && (
            <>
              {modalIndex > 0 && (
                <button
                  onClick={() => scrollToModalIndex(modalIndex - 1)}
                  className="krunk-absolute krunk-left-4 krunk-top-1/2 krunk-transform krunk--translate-y-1/2 krunk-text-white krunk-text-2xl krunk-w-12 krunk-h-12 krunk-flex krunk-items-center krunk-justify-center krunk-rounded-full krunk-bg-black krunk-bg-opacity-50 hover:krunk-bg-opacity-70 krunk-transition-all"
                >
                  ‹
                </button>
              )}
              {modalIndex < displayImages.length - 1 && (
                <button
                  onClick={() => scrollToModalIndex(modalIndex + 1)}
                  className="krunk-absolute krunk-right-4 krunk-top-1/2 krunk-transform krunk--translate-y-1/2 krunk-text-white krunk-text-2xl krunk-w-12 krunk-h-12 krunk-flex krunk-items-center krunk-justify-center krunk-rounded-full krunk-bg-black krunk-bg-opacity-50 hover:krunk-bg-opacity-70 krunk-transition-all"
                >
                  ›
                </button>
              )}
            </>
          )}

          {/* Bottom indicators */}
          {displayImages.length > 1 && (
            <div className="krunk-absolute krunk-bottom-0 krunk-left-0 krunk-right-0 krunk-flex krunk-justify-center krunk-p-4 krunk-bg-gradient-to-t krunk-from-black krunk-from-50% krunk-to-transparent">
              <div className="krunk-flex krunk-gap-2">
                {displayImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => scrollToModalIndex(index)}
                    className={`krunk-w-2 krunk-h-2 krunk-rounded-full krunk-transition-colors ${
                      index === modalIndex ? 'krunk-bg-white' : 'krunk-bg-white krunk-bg-opacity-50'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Zoom controls */}
          <div className="krunk-absolute krunk-bottom-20 krunk-right-4 krunk-flex krunk-flex-col krunk-gap-2">
            <button
              onClick={(e) => {
                const rect = imageRef.current?.getBoundingClientRect();
                if (rect) {
                  handleZoom(0.5, rect.left + rect.width / 2, rect.top + rect.height / 2);
                }
              }}
              className="krunk-text-white krunk-text-xl krunk-w-10 krunk-h-10 krunk-flex krunk-items-center krunk-justify-center krunk-rounded-full krunk-bg-black krunk-bg-opacity-50 hover:krunk-bg-opacity-70 krunk-transition-all"
              disabled={zoomLevel >= 4}
            >
              +
            </button>
            <button
              onClick={() => {
                if (zoomLevel > 1) {
                  setZoomLevel(Math.max(zoomLevel - 0.5, 1));
                  if (zoomLevel - 0.5 <= 1) {
                    setPanOffset({ x: 0, y: 0 });
                  }
                }
              }}
              className="krunk-text-white krunk-text-xl krunk-w-10 krunk-h-10 krunk-flex krunk-items-center krunk-justify-center krunk-rounded-full krunk-bg-black krunk-bg-opacity-50 hover:krunk-bg-opacity-70 krunk-transition-all"
              disabled={zoomLevel <= 1}
            >
              −
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        .krunk-no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .krunk-no-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
};

// Demo component - this simulates how you'd use it with real API data


export default ScrollableImageGallery;